import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/layout/app-shell'
import { useAdminNavigation } from '@/features/admin/shell/navigation'
import {
  portalAccountChangedEventName,
  readStoredPortalAccount,
  type PortalAccountChangedDetail,
} from '@/lib/auth/session-storage'

interface AdminShellPageProps {
  activePath: string
  children?: ReactNode
}

export function AdminShellPage({ activePath, children }: AdminShellPageProps) {
  const { t } = useTranslation('shell')
  const navigation = useAdminNavigation()
  const [user, setUser] = useState(() => readStoredPortalAccount('admin'))

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const syncUser = () => {
      setUser(readStoredPortalAccount('admin'))
    }

    const handleAccountChange = (event: Event) => {
      const detail = (event as CustomEvent<PortalAccountChangedDetail | undefined>).detail

      if (!detail || detail.portal === 'admin') {
        syncUser()
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'admin_data') {
        syncUser()
      }
    }

    window.addEventListener(portalAccountChangedEventName, handleAccountChange)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(portalAccountChangedEventName, handleAccountChange)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  return (
    <AppShell
      activePath={activePath}
      navigation={navigation}
      navigationExpandedGroupsPersistenceKey="admin-navigation:v1"
      portal="admin"
      user={user}
    >
      {children ?? (
        <section
          aria-label={t('adminWorkspace')}
          className="h-full overflow-hidden"
          data-testid="admin-workspace"
        />
      )}
    </AppShell>
  )
}
